import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, shareReplay, switchMap, map } from 'rxjs';
import { BudgetLine } from '@models';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class BudgetService {
  public all$: Observable<BudgetLine[]>;

  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
  ) {
    this.all$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<BudgetLine>(
          this.tenantContext.collections.ACCOUNTING_BUDGET,
        ),
      ),
      shareReplay(1),
    );
  }

  forSeason$(seasonId: string): Observable<BudgetLine[]> {
    return this.all$.pipe(
      map((lines) => lines.filter((l) => l.seasonId === seasonId)),
    );
  }

  /** Filter by fiscal year (e.g. "2026") — matches both "2026" and "2025-2026" */
  forYear$(fiscalYear: string): Observable<BudgetLine[]> {
    return this.all$.pipe(
      map((lines) =>
        lines.filter(
          (l) =>
            l.seasonId === fiscalYear ||
            l.seasonId?.split('-')[1] === fiscalYear,
        ),
      ),
    );
  }

  async create(
    data: Omit<BudgetLine, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<string> {
    return this.firestore.create<BudgetLine>(
      this.tenantContext.collections.ACCOUNTING_BUDGET,
      data,
    );
  }

  async update(data: Partial<BudgetLine> & { id: string }): Promise<void> {
    return this.firestore.update<BudgetLine>(
      this.tenantContext.collections.ACCOUNTING_BUDGET,
      data,
    );
  }

  async delete(id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.ACCOUNTING_BUDGET,
      id,
    );
  }

  /**
   * Clone all budget lines from one season to another.
   * Preserves the parent/child structure by mapping old IDs to new IDs.
   * Throws if the target season already has lines.
   */
  async clone(sourceSeasonId: string, targetSeasonId: string): Promise<void> {
    const sourceLines = await firstValueFrom(this.forYear$(sourceSeasonId));
    if (sourceLines.length === 0) return;

    const targetLines = await firstValueFrom(this.forYear$(targetSeasonId));
    if (targetLines.length > 0) {
      throw new Error('TARGET_SEASON_NOT_EMPTY');
    }

    const idMap = new Map<string, string>(); // oldId → newId

    // First pass: create root lines (no parentId)
    await Promise.all(
      sourceLines
        .filter((l) => !l.parentId)
        .map(async (l) => {
          const newId = await this.create({
            seasonId: targetSeasonId,
            type: l.type,
            estimatedAmount: l.estimatedAmount,
            name: l.name,
            order: l.order,
          });
          idMap.set(l.id, newId);
        }),
    );

    // Second pass: create child lines with remapped parentId
    await Promise.all(
      sourceLines
        .filter((l) => !!l.parentId)
        .map((l) =>
          this.create({
            seasonId: targetSeasonId,
            type: l.type,
            estimatedAmount: l.estimatedAmount,
            name: l.name,
            order: l.order,
            parentId: idMap.get(l.parentId!) ?? undefined,
          }),
        ),
    );
  }
}
